import { ConnectRequiredBlock } from '@/features/projects/components/ConnectRequiredBlock';
import { EligibilityCoverageBlock } from '@heidi/agents';
import type { ToolUseItem } from '@heidi/agents/contracts';
import {
  directoryLeafTool,
  isEligibilityLeafTool,
  parseEligibilityPayload,
  type EligibilityPayload,
} from '@heidi/agents/contracts';
import type { Artifact } from '@heidi/agents/data';
import {
  friendlyToolError,
  isRecoverableContextPrompt,
  readConnectRequired,
} from '@heidi/agents/data';
import type { ReactNode } from 'react';

import { toolResultText } from '../../harness/artifactFromTool';
import { isRecord, readToolData } from './shared/readers';
import { toFetchingStatus } from './status';

/**
 * Directory-routed Availity coverage tools (`integrations.apps.read|write`
 * with leaf `integrations.coverage.*`). Returns null for other leaves so
 * the generic tool row can render.
 */
export const renderCoverageEligibilityToolUse = (
  item: ToolUseItem,
  onOpenArtifact?: (artifact: Artifact) => void
): ReactNode => {
  const leaf = directoryLeafTool(item.name, item.input);
  if (!isEligibilityLeafTool(leaf)) return null;

  const status = toFetchingStatus(item.status);
  if (!status) return null;

  if (status === 'fetching') {
    return <EligibilityCoverageBlock status="fetching" />;
  }

  if (status === 'error') {
    const connect = readConnectRequired(item);
    if (connect) {
      return (
        <ConnectRequiredBlock
          payload={connect}
          error={friendlyToolError(toolResultText(item))}
          toolCallId={item.id}
        />
      );
    }
    if (isRecoverableContextPrompt(toolResultText(item))) return null;
    return (
      <EligibilityCoverageBlock
        status="error"
        error={friendlyToolError(toolResultText(item))}
      />
    );
  }

  const payload = readEligibilityPayload(item);
  if (!payload) return null;

  return (
    <EligibilityCoverageBlock
      status="completed"
      payload={payload}
      onViewAll={
        onOpenArtifact
          ? (next) =>
              onOpenArtifact({
                type: 'insurance',
                variant: 'eligibility',
                patients: next.patients,
                provider: next.provider,
                query: next.query,
                warning: next.warning,
              })
          : undefined
      }
    />
  );
};

const readEligibilityPayload = (item: ToolUseItem): EligibilityPayload | null => {
  const data = readToolData(item);
  const input = isRecord(item.input) ? item.input : undefined;
  return parseEligibilityPayload(data, input);
};
